/**
 * ProjectStatus Component
 * Displays detailed project status with timeline and payment info
 * L0-CMD-2026-0120-007 - Wired to API
 */

import React, { useState, useEffect } from 'react';
import PaymentGate from './PaymentGate';

interface ProjectStatusProps {
  projectId: number;
  onPaymentSuccess?: (paymentIntentId: string) => void;
}

interface Project {
  id: number;
  project_number: string;
  name: string;
  client_id: number;
  client_name?: string;
  description?: string;
  address?: string;
  general_contractor?: string;
  status: string;
  progress: number;
  priority: string;
  due_date: string;
  quoted_amount?: number;
  final_amount?: number;
  created_at: string;
  updated_at?: string;
  assigned_to?: string;
  documents?: Array<{
    id: number;
    filename: string;
    document_type: string;
    step_number?: number;
    is_parsed: boolean;
  }>;
  drawings?: Array<{
    id: number;
    drawing_number: string;
    title: string;
    revision: string;
    status: string;
  }>;
}

interface StatusStep {
  name: string;
  status: 'completed' | 'current' | 'pending';
  date?: string;
}

const ProjectStatus: React.FC<ProjectStatusProps> = ({ projectId, onPaymentSuccess }) => {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentGate, setShowPaymentGate] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  const fetchProject = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_BASE}/api/projects/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch project');
      }

      const data = await response.json();
      setProject(data);
    } catch (err) {
      console.error('Failed to fetch project:', err);
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const getStatusSteps = (status: string, progress: number): StatusStep[] => {
    const steps: StatusStep[] = [
      { name: 'Submitted', status: 'completed', date: project?.created_at },
      { name: 'Payment Received', status: 'pending' },
      { name: 'In Production', status: 'pending' },
      { name: 'Quality Review', status: 'pending' },
      { name: 'Client Approval', status: 'pending' },
      { name: 'Completed', status: 'pending' },
    ];

    // Update steps based on status
    switch (status) {
      case 'pending_payment':
        steps[1].status = 'current';
        break;
      case 'queued':
        steps[1].status = 'completed';
        steps[2].status = 'current';
        break;
      case 'in_progress':
        steps[1].status = 'completed';
        steps[2].status = 'current';
        break;
      case 'review':
        steps[1].status = 'completed';
        steps[2].status = 'completed';
        steps[3].status = 'current';
        break;
      case 'client_review':
        steps[1].status = 'completed';
        steps[2].status = 'completed';
        steps[3].status = 'completed';
        steps[4].status = 'current';
        break;
      case 'completed':
        steps.forEach(s => s.status = 'completed');
        break;
      case 'cancelled':
        // Keep as is
        break;
    }

    return steps;
  };

  const handlePaymentSuccess = (paymentIntentId: string) => {
    setShowPaymentGate(false);
    fetchProject(); // Refresh project data
    if (onPaymentSuccess) {
      onPaymentSuccess(paymentIntentId);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending_payment': return 'Awaiting Payment';
      case 'queued': return 'In Queue';
      case 'in_progress': return 'In Production';
      case 'review': return 'Under Review';
      case 'client_review': return 'Awaiting Client Approval';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      case 'on_hold': return 'On Hold';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'var(--success, #10b981)';
      case 'in_progress': return 'var(--primary, #2563eb)';
      case 'review':
      case 'client_review': return 'var(--warning, #f59e0b)';
      case 'pending_payment': return 'var(--danger, #ef4444)';
      case 'cancelled': return 'var(--muted, #475569)';
      default: return 'var(--secondary, #64748b)';
    }
  };

  if (loading) {
    return (
      <div className="project-status loading">
        <div className="loading-spinner"></div>
        <p>Loading project...</p>
        <style>{loadingStyles}</style>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="project-status error">
        <div className="error-icon">!</div>
        <h3>Unable to Load Project</h3>
        <p>{error || 'Project not found'}</p>
        <button className="retry-btn" onClick={fetchProject}>Retry</button>
        <style>{errorStyles}</style>
      </div>
    );
  }

  if (showPaymentGate) {
    const depositAmount = (project.quoted_amount || 0) * 0.5;
    const pageFee = (project.documents?.length || 0) * 1.0;
    const totalDue = depositAmount + pageFee;

    return (
      <PaymentGate
        projectId={project.id}
        depositAmount={depositAmount}
        pageFee={pageFee}
        totalDue={totalDue}
        projectName={project.name}
        tier="standard"
        estimatedHours={Math.round((project.quoted_amount || 0) / 65)}
        hourlyRate={65}
        onPaymentSuccess={handlePaymentSuccess}
        onCancel={() => setShowPaymentGate(false)}
      />
    );
  }

  const statusSteps = getStatusSteps(project.status, project.progress);

  return (
    <div className="project-status">
      <div className="status-header">
        <div className="project-info">
          <span className="project-id">{project.project_number}</span>
          <h2>{project.name}</h2>
          <span className="client-name">{project.client_name || 'Client'}</span>
          <span
            className="status-badge"
            style={{
              backgroundColor: `${getStatusColor(project.status)}20`,
              color: getStatusColor(project.status)
            }}
          >
            {getStatusLabel(project.status)}
          </span>
        </div>
        <div className="progress-ring">
          <svg viewBox="0 0 100 100">
            <circle className="bg" cx="50" cy="50" r="40" />
            <circle
              className="progress"
              cx="50" cy="50" r="40"
              style={{ strokeDasharray: `${project.progress * 2.51} 251` }}
            />
          </svg>
          <span className="progress-text">{project.progress}%</span>
        </div>
      </div>

      {/* Payment CTA for pending payment status */}
      {project.status === 'pending_payment' && (
        <div className="payment-cta">
          <div className="cta-content">
            <h3>Payment Required</h3>
            <p>Complete your payment to start production</p>
            {project.quoted_amount && (
              <div className="amount-due">
                <span>Amount Due:</span>
                <strong>{formatCurrency(project.quoted_amount * 0.5)}</strong>
              </div>
            )}
          </div>
          <button className="pay-btn" onClick={() => setShowPaymentGate(true)}>
            Make Payment
          </button>
        </div>
      )}

      <div className="timeline">
        {statusSteps.map((step, index) => (
          <div
            key={step.name}
            className={`timeline-step ${step.status}`}
          >
            <div className="step-indicator">
              {step.status === 'completed' ? '✓' : index + 1}
            </div>
            <div className="step-content">
              <span className="step-name">{step.name}</span>
              {step.date && step.status === 'completed' && (
                <span className="step-date">{formatDate(step.date)}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="project-details">
        {project.assigned_to && (
          <div className="detail-row">
            <span className="label">Assigned To</span>
            <span className="value">{project.assigned_to}</span>
          </div>
        )}
        <div className="detail-row">
          <span className="label">Created</span>
          <span className="value">{formatDate(project.created_at)}</span>
        </div>
        {project.due_date && (
          <div className="detail-row">
            <span className="label">Due Date</span>
            <span className="value">{formatDate(project.due_date)}</span>
          </div>
        )}
        {project.quoted_amount && (
          <div className="detail-row">
            <span className="label">Quoted Amount</span>
            <span className="value">{formatCurrency(project.quoted_amount)}</span>
          </div>
        )}
        {project.documents && project.documents.length > 0 && (
          <div className="detail-row">
            <span className="label">Documents</span>
            <span className="value">{project.documents.length} files</span>
          </div>
        )}
      </div>

      <style>{`
        .project-status {
          background: var(--bg-secondary, #1e293b);
          border-radius: 12px;
          padding: 24px;
        }

        .status-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .project-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .project-id {
          font-size: 12px;
          color: var(--text-secondary, #94a3b8);
          font-family: monospace;
        }

        .project-info h2 {
          margin: 0;
          font-size: 20px;
          color: var(--text-primary, #f1f5f9);
        }

        .client-name {
          color: var(--text-secondary, #94a3b8);
          margin-bottom: 8px;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          width: fit-content;
        }

        .progress-ring {
          position: relative;
          width: 80px;
          height: 80px;
          flex-shrink: 0;
        }

        .progress-ring svg {
          transform: rotate(-90deg);
          width: 100%;
          height: 100%;
        }

        .progress-ring circle {
          fill: none;
          stroke-width: 8;
        }

        .progress-ring .bg {
          stroke: var(--bg-tertiary, #334155);
        }

        .progress-ring .progress {
          stroke: var(--primary, #2563eb);
          stroke-linecap: round;
          transition: stroke-dasharray 0.3s;
        }

        .progress-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-weight: 600;
          color: var(--text-primary, #f1f5f9);
        }

        .payment-cta {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid var(--danger, #ef4444);
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .cta-content h3 {
          margin: 0 0 4px 0;
          color: var(--danger, #ef4444);
          font-size: 16px;
        }

        .cta-content p {
          margin: 0;
          color: var(--text-secondary, #94a3b8);
          font-size: 14px;
        }

        .amount-due {
          margin-top: 8px;
          font-size: 14px;
        }

        .amount-due span {
          color: var(--text-secondary, #94a3b8);
        }

        .amount-due strong {
          color: var(--text-primary, #f1f5f9);
          margin-left: 8px;
          font-size: 18px;
        }

        .pay-btn {
          background: var(--danger, #ef4444);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .pay-btn:hover {
          background: #dc2626;
        }

        .timeline {
          display: flex;
          flex-direction: column;
          gap: 0;
          margin-bottom: 24px;
        }

        .timeline-step {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 16px 0;
          position: relative;
        }

        .timeline-step:not(:last-child)::after {
          content: '';
          position: absolute;
          left: 15px;
          top: 48px;
          width: 2px;
          height: calc(100% - 32px);
          background: var(--bg-tertiary, #334155);
        }

        .timeline-step.completed:not(:last-child)::after {
          background: var(--success, #10b981);
        }

        .step-indicator {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          flex-shrink: 0;
          z-index: 1;
        }

        .timeline-step.completed .step-indicator {
          background: var(--success, #10b981);
          color: white;
        }

        .timeline-step.current .step-indicator {
          background: var(--primary, #2563eb);
          color: white;
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.2);
        }

        .timeline-step.pending .step-indicator {
          background: var(--bg-tertiary, #334155);
          color: var(--text-secondary, #94a3b8);
        }

        .step-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .step-name {
          font-weight: 500;
          color: var(--text-primary, #f1f5f9);
        }

        .timeline-step.pending .step-name {
          color: var(--text-secondary, #94a3b8);
        }

        .step-date {
          font-size: 12px;
          color: var(--text-secondary, #94a3b8);
        }

        .project-details {
          border-top: 1px solid var(--border-color, #334155);
          padding-top: 24px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
        }

        .detail-row .label {
          color: var(--text-secondary, #94a3b8);
        }

        .detail-row .value {
          font-weight: 500;
          color: var(--text-primary, #f1f5f9);
        }

        @media (max-width: 480px) {
          .status-header {
            flex-direction: column;
            gap: 16px;
          }

          .payment-cta {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .pay-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

const loadingStyles = `
  .project-status.loading {
    background: var(--bg-secondary, #1e293b);
    border-radius: 12px;
    padding: 48px;
    text-align: center;
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 3px solid var(--bg-tertiary, #334155);
    border-top-color: var(--primary, #2563eb);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .project-status.loading p {
    color: var(--text-secondary, #94a3b8);
    margin: 0;
  }
`;

const errorStyles = `
  .project-status.error {
    background: var(--bg-secondary, #1e293b);
    border-radius: 12px;
    padding: 48px;
    text-align: center;
  }

  .error-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.2);
    color: var(--danger, #ef4444);
    font-size: 32px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
  }

  .project-status.error h3 {
    margin: 0 0 8px 0;
    color: var(--text-primary, #f1f5f9);
  }

  .project-status.error p {
    color: var(--text-secondary, #94a3b8);
    margin: 0 0 24px 0;
  }

  .retry-btn {
    background: var(--primary, #2563eb);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .retry-btn:hover {
    background: #1d4ed8;
  }
`;

export default ProjectStatus;
